import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Lock, Mail, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { FLASH_KEY } from "../lib/api";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Banner text set from a one-shot flash the axios 401 interceptor stashed
  // BEFORE it redirected here. Rendered inline (not as a toast) so it survives
  // the hard navigation regardless of Toaster mount timing.
  const [sessionNotice, setSessionNotice] = useState("");

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem(FLASH_KEY);
      if (msg) {
        sessionStorage.removeItem(FLASH_KEY);
        setSessionNotice(msg);
        // Also fire a toast (best-effort — banner is the reliable path)
        setTimeout(() => toast.info(msg, { duration: 5000 }), 300);
      }
    } catch (_) { /* storage disabled — silent */ }
  }, []);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grain relative flex items-center justify-center px-4">
      {/* Background accents */}
      <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-blue-500/20 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-emerald-500/15 blur-[120px] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="glass-strong rounded-[2rem] p-8 md:p-10 w-full max-w-md relative z-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500/40 to-emerald-500/30 border border-white/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-heading text-2xl tracking-tight text-white leading-none">WrapConsole</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">Vinyl wrap operations</div>
          </div>
        </div>

        <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tighter text-white mb-1">
          Welcome back
        </h1>
        <p className="text-slate-400 text-sm mb-8">Sign in to manage your shop floor.</p>

        {sessionNotice && (
          <div
            data-testid="login-session-notice"
            className="mb-6 flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2.5 text-amber-100 text-sm"
            role="status"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{sessionNotice}</span>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-label block mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20"
                placeholder="you@workshop.com"
              />
            </div>
          </div>

          <div>
            <label className="text-label block mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-white text-black font-semibold rounded-full py-3 hover:bg-slate-100 transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <div className="pt-2 text-center">
            <a
              href="/signup"
              data-testid="login-signup-link"
              className="text-xs text-slate-400 hover:text-white transition"
            >
              New to WrapConsole? <span className="text-white font-semibold">Start your 14-day free trial →</span>
            </a>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
